// Direction B "Ink Header" message bubble. Two variants:
//   - Agent (left-aligned): muted-100 bg, ink text, top-LEFT corner clipped to 4px
//   - User  (right-aligned): ink bg, white text, top-RIGHT corner clipped to 4px
// Both: 14px / 1.45 / max 264px / 9px×13px padding / 14px corner radius
//
// The corner-clip on the same edge as the speaker is the "speech ear" — makes
// the bubble feel anchored to its sender. It's intentional that both variants
// have one rounded-2xl edge AND one rounded-sm edge (4px) to read as paired.
//
// Per spec §7.3: plain text + autolink. NO markdown. The LLM emitting **bold**
// renders literal asterisks — tone discipline lives in the prompt, not here.

import { autolink } from '../lib/autolink'

export interface TextBubbleProps {
  role: 'user' | 'assistant'
  children: string
}

export function TextBubble({ role, children }: TextBubbleProps) {
  const segments = autolink(children)
  const isAgent = role === 'assistant'

  return (
    <div
      data-testid="text-bubble"
      data-role={role}
      style={{ whiteSpace: 'pre-wrap' }}
      className={[
        // Shared
        'text-sm leading-[1.45] px-[13px] py-[9px] rounded-2xl',
        'max-w-[264px] break-words animate-bubbleIn',
        // Variant
        isAgent
          ? 'self-start bg-muted-100 text-ink rounded-tl-[4px]'
          : 'self-end bg-ink text-white rounded-tr-[4px]',
      ].join(' ')}
    >
      {segments.map((seg, i) =>
        seg.type === 'url' ? (
          <a
            key={i}
            href={seg.value}
            target="_blank"
            rel="noopener noreferrer ugc"
            className={isAgent ? 'text-accent underline' : 'text-white underline underline-offset-2'}
          >
            {seg.value}
          </a>
        ) : (
          <span key={i}>{seg.value}</span>
        )
      )}
    </div>
  )
}
